import { Component, inject } from '@angular/core';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { BasePageComponent } from '../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../_shared/components/filters/date-filter/date-filter.component';
import { SearchComponent } from '../../_shared/components/filters/search/search.component';
import { LookupLabelPipe } from '../../_shared/pipes/lookup-label.pipe';
import { translateLookupLabel } from '../../_shared/helpers/lookup-label.helper';
import { BUDGET_CATEGORY_LABELS, BudgetCategory } from './models/cost-tracking-request.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../_shared/models';
import { DateInterval } from '../../_shared/enums';
import { FilterService } from '../../_shared/services/filter.service';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import { CostTrackingTabType } from '../_shared/models/tabs.models';
import { TotalRepairCostsComponent } from './components/widgets/total-repair-costs/total-repair-costs.component';
import { TotalInsuranceCostComponent } from './components/widgets/total-insurance-cost/total-insurance-cost.component';
import { TotalComplianceCostsComponent } from './components/widgets/total-compliance-costs/total-compliance-costs.component';
import { AnnualBudgetComponent } from './components/widgets/annual-budget/annual-budget.component';
import { CostDistributionReactivePreventiveComponent } from './components/graphs/cost-distribution-reactive-preventive/cost-distribution-reactive-preventive.component';
import { CostDistributionCategoryComponent } from './components/cost-distribution-category/cost-distribution-category.component';
import { MaintenanceTrendComponent } from './components/graphs/maintenance-trend/maintenance-trend.component';
import { BudgetedVsActualComponent } from './components/graphs/budgeted-vs-actual/budgeted-vs-actual.component';
import { AssetCostTableComponent } from './components/asset-cost-table/asset-cost-table.component';
import { BudgetTableComponent } from './components/budget-table/budget-table.component';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { SetBudgetModalComponent } from './components/modals/set-budget-modal/set-budget-modal.component';
import { CostTrackingService } from './services/cost-tracking.service';
import { BudgetItem } from './models/cost-tracking-response.model';
import { getSelectedFilterLabel } from '../../_shared/helpers/kpiWidget/selected-filter.helper';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-cost-tracking',
  standalone: true,
  imports: [
    TranslocoDirective,
    CommonModule,
    NgbDropdownModule,
    LookupLabelPipe,
    AccountFilterComponent,
    DateFilterComponent,
    SearchComponent,
    TotalRepairCostsComponent,
    TotalInsuranceCostComponent,
    TotalComplianceCostsComponent,
    AnnualBudgetComponent,
    CostDistributionReactivePreventiveComponent,
    CostDistributionCategoryComponent,
    MaintenanceTrendComponent,
    BudgetedVsActualComponent,
    AssetCostTableComponent,
    BudgetTableComponent
  ],
  templateUrl: './cost-tracking.component.html',
  styleUrl: './cost-tracking.component.scss'
})
export class CostTrackingComponent extends BasePageComponent {
  private readonly transloco = inject(TranslocoService);
  private filterService: FilterService = inject(FilterService);
  private modalService = inject(NgbModal);
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);

  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];

  readonly showCostDistributionTab: boolean = false;

  selectedTab: CostTrackingTabType = this.showCostDistributionTab ? 'cost-distribution' : 'budget';

  private readonly visitedTabs = new Set<CostTrackingTabType>([this.selectedTab]);

  assetCostSearchTerm: string = '';

  protected readonly budgetCategoryLabels = BUDGET_CATEGORY_LABELS;
  budgetSearchTerm: string = '';
  budgetCategories: BudgetCategory[] = [];
  selectedBudgetCategory: BudgetCategory | null = null;
  isLoadingBudgetCategories: boolean = false;

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.selectedTab === 'budget') {
      this.loadBudgetCategories();
    }
  }

  getSelectedFilterLabel(): string {
    return getSelectedFilterLabel(this.dateFilter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();
    }
  }

  onTabChange(tab: CostTrackingTabType): void {
    this.selectedTab = tab;
    this.visitedTabs.add(tab);

    if (tab === 'budget') {
      this.loadBudgetCategories();
    }
  }

  hasVisited(tab: CostTrackingTabType): boolean {
    return this.visitedTabs.has(tab);
  }

  onAssetCostSearchTermUpdate(searchTerm: string): void {
    this.assetCostSearchTerm = searchTerm ?? '';
  }

  onBudgetSearchTermUpdate(searchTerm: string): void {
    this.budgetSearchTerm = searchTerm ?? '';
  }

  onBudgetCategorySelect(category: BudgetCategory | null): void {
    this.selectedBudgetCategory = category;
  }

  get budgetCategoryLabel(): string {
    if (this.isLoadingBudgetCategories) {
      return this.transloco.translate('costTracking.budgetTable.filters.loadingCategories');
    }

    return translateLookupLabel(this.transloco, this.budgetCategoryLabels, this.selectedBudgetCategory?.name)
      || this.transloco.translate('costTracking.budgetTable.filters.allCategories');
  }

  private async loadBudgetCategories(): Promise<void> {
    if (this.budgetCategories.length) return;

    this.isLoadingBudgetCategories = true;

    try {
      const response = await this.costTrackingService.getBudgetCategories();
      this.budgetCategories = response.data ?? [];
    } catch (error) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoadingBudgetCategories = false;
    }
  }

  isTabActive(tab: CostTrackingTabType): boolean {
    return this.selectedTab === tab;
  }

  onSetBudget(): void {
    this.openSetBudgetModal();
  }

  onEditBudget(budget: BudgetItem): void {
    this.openSetBudgetModal(budget);
  }

  private openSetBudgetModal(budget?: BudgetItem): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static'
    };

    const modalRef = this.modalService.open(SetBudgetModalComponent, modalOptions);
    if (budget) {
      modalRef.componentInstance.budget = budget;
    }
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter
    }
  }

  protected override applyPageFilterState(pageFilter: any): void { }
}